import React, { useCallback, useContext, useEffect, useState } from "react";
import { GetColumnsWithGroupResponse } from "../models/windows/GetColumnsWithGroup";
import MaterialIssueDetails from "../components/MaterialIssueDetails";
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFab,
  IonFabButton,
  IonHeader,
  IonIcon,
  IonLoading,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonAlert,
  useIonToast,
} from "@ionic/react";
import MaterialIssueItemList from "../components/MaterialIssueItemList";
import { add, chevronBackOutline } from "ionicons/icons";
import OnlineStatus from "../components/OnlineStatus";
import { MaterialIssueListItem } from "../models/materialissue/MaterialIssue";
import { ReturnMaterialsBody, returnMaterials } from "../api/Materials";
import { useIssueReturnManager } from "../hooks/useIssueReturnManager";
import MaterialIssueItemSearch from "../components/MaterialItemSearch";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { useWorkOrderStore } from "../store/wo-order";
import useGetPlannedListItems from "../hooks/work-order/issue-return/useGetPlannedListItems";
import { useOfflineStore } from "../store/offline-mode";
import { useSettingsStore } from "../store/settings";
import { useWoOfflineUpdatesStore } from "../store/wo-offline-updates";
import useGetReturnDetailsColumns from "../hooks/work-order/issue-return/useGetReturnDetailsColumns";
import { TranslationMessagesContext } from "../util/Translations";
import { useHistory, useParams } from "react-router";
import { ItemListEntry } from "./MaterialIssue";
import { ApiResponse } from "../models/api/ApiResponse";

const IssueReturn: React.FC = () => {
  const {
    employees,
    employeeId,
    storeLocation,
    itemList,
    dateCharged,
    searchOpen,
    displayWo,
    displayReq,
    accountObject,
    translations,
    showLoading,
    setShowLoading,
    setEmployeeId,
    setStoreLocation,
    setItemList,
    setDateCharged,
    setSearchOpen,
    setupDetails,
    onItemSelect,
    onSelectAll,
    onBinAmountChange,
    onResponse,
    onComment,
    onItemAdd,
  } = useIssueReturnManager();
  const { offlineReturnMaterialId } = useParams<{
    workOrderId: string;
    requisitionId: string;
    accountSelections: string;
    offlineReturnMaterialId: string;
  }>();

  const [issueDetailsColumns, setIssueDetailsColumns] =
    useState<GetColumnsWithGroupResponse>();
  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const history = useHistory();

  const { getPlannedListItems: getPlannedListItemsForReturn } =
    useGetPlannedListItems();
  const isOffline = useOfflineStore((state) => state.isOffline);
  const defaultStore = useSettingsStore((state) => state.defaultStore);
  const addReturn = useWoOfflineUpdatesStore((state) => state.addReturn);
  const { getReturnDetailsColumns } = useGetReturnDetailsColumns();
  const offlineReturn = useWoOfflineUpdatesStore((state) =>
    state.getReturnById(offlineReturnMaterialId)
  );
  const makeOperationCompletedById = useWoOfflineUpdatesStore(
    (state) => state.makeOperationCompletedById
  );
  const updateStatusById = useWoOfflineUpdatesStore(
    (state) => state.updateStatusById
  );

  useEffect(() => {
    setup();
  }, []);

  const getOfflineIssueItems = useCallback(
    (items: ItemListEntry[]) => {
      if (!offlineReturn || !offlineReturn.MaterialReturnList?.length)
        return items;
      return offlineReturn.MaterialReturnList.reduce(
        (list: ItemListEntry[], curr) => {
          const item = items.find((i) => i.item.Item === curr.Item);
          if (item) {
            let updatedItem = { ...item };
            updatedItem.binData = updatedItem.binData.map((b) => {
              const updateBin = curr.Bins.find((cb) => cb.Bin === b.Bin);
              if (updateBin)
                b = {
                  ...b,
                  TransactionQuantity: updateBin.TransactionQuantity,
                };
              return b;
            });
            updatedItem = {
              ...updatedItem,
              comments: curr.Comments,
              checked: true,
            };

            list.push(updatedItem);
          }

          return list;
        },
        []
      );
    },
    [offlineReturn]
  );

  const getPlannedListItems = useCallback(
    async (storesLocation: string, workOrder: string, requisition: string) => {
      const issueItemList = useWorkOrderStore
        .getState()
        .getReturnItemForWo(displayWo);

      if (issueItemList && isOffline) {
        setItemList(getOfflineIssueItems([...issueItemList]));
        return; //got it in the cache
      }
      setShowLoading(true);
      const { response, items } = await getPlannedListItemsForReturn({
        requisition,
        storesLocation,
        workOrder,
        isIssueMaterial: false,
      });
      if(response.isError || (response.data && !response.data.Success)){
        presentAlert({
          message: response.data?.MessageList && response.data.MessageList.length > 0 && response.data.MessageList[0].Text,
          buttons: [
            {
              text: translations["lbl_btn_ok"] || "OK",
            },
          ],
        });
        setShowLoading(false);
        return;
      }
      const filteredItems = items.filter(
        (item) => item.item.LineQuantityIssued > item.item.LineQuantityReturned
      );
      setItemList(getOfflineIssueItems([...filteredItems]));
      setShowLoading(false);
    },
    [
      displayWo,
      getOfflineIssueItems,
      getPlannedListItemsForReturn,
      setItemList,
      setShowLoading,
    ]
  );

  useEffect(() => {
    if (storeLocation && (displayWo || displayReq)) {
      getPlannedListItems(storeLocation, displayWo, displayReq);
    } else if (isOffline && displayWo && defaultStore) {
      getPlannedListItems(defaultStore, displayWo, displayReq);
    }
  }, [
    storeLocation,
    displayWo,
    displayReq,
    getPlannedListItems,
    isOffline,
    defaultStore,
  ]);

  useEffect(() => {
    if (offlineReturn) {
      //feed offline date
      setDateCharged(offlineReturn.ReturnDate ?? "");
    }
  }, [offlineReturn, setDateCharged]);

  const setup = async () => {
    const columns = useWorkOrderStore.getState().returnColumns;
    setShowLoading(true);
    const columnDetails = columns ?? (await getReturnDetailsColumns()).data;
    setIssueDetailsColumns(columnDetails);
    setupDetails();
    setShowLoading(false);
  };

  const handleReturnFailResponse = (response: ApiResponse) => {
    const errorList: any[] = response.data.filter((d: any) => !d.Success) ?? [];

    if (errorList.length) {
      const strings =
        errorList.flatMap((e) =>
          e?.MessageList?.map((message: { Text: string }) => {
            return message.Text;
          })
        ) ?? [];
      if (strings?.length)
        presentToast({
          message: strings.join(" "),
          color: "danger",
          duration: 5000,
          position: "top",
        });
    }
  };

  const isReadyToIssueReturn = () => {
    const checkedList = itemList.filter((entry) => {
      return entry.checked;
    });
    if (checkedList.length === 0) {
      return false;
    }
    let amount = 0;
    for (const entry of checkedList) {
      for (const bin of entry.binData) {
        amount += bin.TransactionQuantity || 0;
      }
    }
    return amount > 0;
  };

  const issueReturn = async () => {
    if (!storeLocation || !employeeId) {
      return;
    }
    const materialIssueList: MaterialIssueListItem[] = [];
    const selectedItems = itemList.filter((entry) => {
      return entry.checked;
    });
    selectedItems.forEach((entry, index) => {
      const bins = [];
      for (const bin of entry.binData) {
        if (bin.TransactionQuantity && bin.TransactionQuantity > 0) {
          bins.push({
            Bin: bin.Bin,
            MaterialIssueId: bin.ObjectId,
            RequisitionLine: entry.item.RequisitionLine,
            TransactionQuantity: bin.TransactionQuantity,
          });
        }
      }
      if (bins.length > 0) {
        materialIssueList.push({
          Comments: entry.comments,
          Item: entry.item.Item,
          ItemIndex: index,
          Requisition: entry.item.Requisition,
          RequisitionLine: entry.item.RequisitionLine,
          SerialIds: [],
          StoresLocation: storeLocation,
          Bins: bins,
        });
      }
    });

    const body: ReturnMaterialsBody = {
      WorkOrder: displayWo,
      ReturnDate: dateCharged,
      ReturnFromUser: employeeId?.EmployeeId,
      Account: {
        Account:
          !!accountObject && !!accountObject.Account
            ? accountObject.Account
            : "",
        Area: !!accountObject && !!accountObject.Area ? accountObject.Area : "",
        Department:
          !!accountObject && !!accountObject.Department
            ? accountObject.Department
            : "",
        Project:
          !!accountObject && !!accountObject.Project
            ? accountObject.Project
            : "",
      },
      MaterialReturnList: materialIssueList,
    };

    if (isOffline) {
      addReturn({ ...body, woNumber: displayWo });
      presentAlert({
        header: `${
          translatedMessages["MobReturnMaterialSaveSuccess"]?.MessageText ||
          "Return Material Saved Successfully In Offline Store."
        }`,
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
            handler: () => {
              // Navigate back
              history.goBack();
            },
          },
        ],
      });
      return; //we will sync when user is back online
    }

    setShowLoading(true);
    const response = await returnMaterials(body);
    setShowLoading(false);
    if (response.isError) {
      if (offlineReturnMaterialId)
        updateStatusById(offlineReturnMaterialId, "failed", [
          String(response.data),
        ]);
      presentToast({
        message: String(response.data),
        duration: 5000,
        color: "error",
      });
    } else if (offlineReturnMaterialId) {
      makeOperationCompletedById(offlineReturnMaterialId); //remove from offline store
      history.goBack();
    } else {
      var newItemList = [...itemList];
      let updatedItemList = [...newItemList];
      response.data.forEach((issuedData: any) => {
        if (issuedData.Success) {
          const parts = issuedData.MaterialTransactionId.split("|");
          const itemValue = parts[1];
          const binVal = parts[3];
          updatedItemList = updatedItemList.map((entry) => {
            const totalTransactionQuantity = entry.binData?.reduce(
              (sum, binData) => sum + (binData.TransactionQuantity || 0),
              0
            );
            const newItem = {
              ...entry.item,
              LineQuantityReturned:
                entry.item.LineQuantityReturned + totalTransactionQuantity,
            };
            if (
              entry.item.Item === itemValue &&
              entry.item.RequisitionLine === issuedData.ReqLine &&
              entry.checked
            ) {
              const newBinList = entry.binData?.map((binData) => {
                return { ...binData, TransactionQuantity: 0 };
              });
              return {
                ...entry,
                binData: newBinList,
                item: newItem,
                checked: false,
              };
            }
            return entry;
          });
        } else {
          handleReturnFailResponse(response);
        }
      });
      globalThis.fromReturn = true;
      onResponse(response.data, updatedItemList);
      globalThis.fromReturn = false;
    }
  };

  return (
    <>
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/home" icon={chevronBackOutline} />
            </IonButtons>
            <IonTitle>
              {displayWo || displayReq || accountObject.Account}
            </IonTitle>
            <IonButtons slot="end">
              <IonButton
                color="primary"
                disabled={!isReadyToIssueReturn()}
                onClick={issueReturn}
              >
                {translations["lbl_btn_return"] || "Return"}
              </IonButton>
            </IonButtons>
          </IonToolbar>
          <OnlineStatus />
        </IonHeader>

        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          duration={5000}
        />

        <IonContent class="ion-padding">
          {issueDetailsColumns && (
            <MaterialIssueDetails
              columns={issueDetailsColumns}
              storeLocation={storeLocation}
              setStoreLocation={setStoreLocation}
              employeeId={employeeId}
              setEmployeeId={setEmployeeId}
              employees={employees}
              date={dateCharged}
              setDate={setDateCharged}
              mode="return"
              displayWo={displayWo}
              displayReq={displayReq}
            />
          )}

          <div className="ion-margin-bottom"></div>

          <MaterialIssueItemList
            itemList={itemList}
            onSelect={onItemSelect}
            onSelectAll={onSelectAll}
            onBinAmountChange={onBinAmountChange}
            mode="return"
            handleComment={onComment}
          />

        </IonContent>
      </IonPage>
      {searchOpen && (
        <MaterialIssueItemSearch
          isOpen={searchOpen}
          onSelect={onItemAdd}
          onCancel={() => {
            setSearchOpen(false);
          }}
          storeLocation={storeLocation}
          issueOrReturn="return"
        />
      )}
    </>
  );
};

export default withAITracking(reactPlugin, IssueReturn, "IssueReturn");
